import {
  DEADLINE_BUCKETS,
  DEADLINE_DOMAIN_VALUES,
  type DeadlineBucket,
  type DeadlineDomainValue,
  type DeadlineManagedByValue,
  type RecurrenceAnchorValue,
} from './deadline.constants';
import type { DeadlineDocumentSignatureState } from './deadline-management.constants';

export const CONSULTANT_DEADLINE_BUCKETS = DEADLINE_BUCKETS;
export type ConsultantDeadlineBucket = DeadlineBucket;

export const CONSULTANT_DEADLINE_DOMAINS = DEADLINE_DOMAIN_VALUES;
export type ConsultantDeadlineDomain = DeadlineDomainValue;

/** Finestra "in scadenza" usata dal digest e dal contatore `dueWithin30` del backend. */
export const CONSULTANT_DEADLINE_DUE_WINDOW_DAYS = 30;
export const CONSULTANT_DEADLINES_SUMMARY_MAX_PAGE_SIZE = 50;
export const CONSULTANT_DEADLINES_LIST_MAX_PAGE_SIZE = 200;
export const CONSULTANT_DEADLINES_DEFAULT_PAGE_SIZE = 50;

export interface ConsultantDeadlineCounters {
  total: number;
  /** Data passata, anche se la riga è in rinnovo (resta tra gli scaduti con l'etichetta). */
  expired: number;
  /** Non scaduta, entro il preavviso massimo della riga e non in rinnovo. */
  expiring: number;
  /** Presa in carico attiva. */
  inRenewal: number;
  ok: number;
  noDeadline: number;
}

export interface ConsultantDeadlineTenantSummary {
  tenantId: string;
  tenantName: string;
  counters: ConsultantDeadlineCounters;
  /** L'azienda usa Prospero (attiva o in prova, non gestita dal consulente, con chi firma): si può chiedere la firma. */
  canRequestCompanySignature: boolean;
}

export interface ConsultantDeadlineSummaryResponse {
  items: ConsultantDeadlineTenantSummary[];
  /** Clienti nel perimetro (non solo quelli della pagina). */
  total: number;
  /** Clienti del perimetro con almeno una riga che rispetta i filtri (con o senza filtri di riga). */
  clientsWithDeadlines: number;
  page: number;
  pageSize: number;
  /** Somma dei contatori di TUTTO il perimetro filtrato, non della pagina. */
  totals: ConsultantDeadlineCounters;
}

export interface ConsultantDeadlineRow {
  id: string;
  tenantId: string;
  tenantName: string;
  documentTypeId: string | null;
  documentTypeName: string | null;
  /** Titolo libero (solo tipo "Altro"). */
  title: string | null;
  /** Nome da mostrare: il titolo libero per "Altro", il nome del tipo per gli altri tipi. */
  displayName?: string;
  domain: ConsultantDeadlineDomain;
  /** `YYYY-MM-DD` oppure null per i tipi senza data. */
  dueAt: string | null;
  /** Etichetta esclusiva: EXPIRED > IN_RENEWAL > EXPIRING > OK; NO_DEADLINE se senza data. */
  bucket: ConsultantDeadlineBucket;
  /** `YYYY-MM-DD` se la presa in carico è attiva, altrimenti null. */
  inRenewalUntil: string | null;
  managedBy: DeadlineManagedByValue;
  assigneeUserId: string | null;
  /** `null` se la persona è anonimizzata o rimossa (la UI mostra "Utente rimosso"). */
  assigneeName: string | null;
  hasDocument: boolean;
  /** Nome del file dell'ultimo ciclo con file (anche precedente al corrente): abilita "Apri documento". */
  documentFilename: string | null;
  warningDays: number[];
  /** Se `false` il cliente non può caricare il rinnovo di una riga gestita dal consulente. */
  clientCanUpload: boolean;
  /** Ogni nuovo file della riga chiede la firma dell'azienda. */
  requestClientSignature: boolean;
  /** Come `ConsultantDeadlineTenantSummary.canRequestCompanySignature`, per il cliente della riga. */
  canRequestCompanySignature: boolean;
  /** Firma dell'ultimo file con documento; `null` senza documento. Non cambia lo stato della scadenza. */
  documentSignature: DeadlineDocumentSignatureState | null;
  /** La riga è gestita dal consulente che sta leggendo (non da un altro consulente dello stesso cliente). */
  isManagedByMe: boolean;
  /** La riga è gestita da un collega del mio studio (stesso partner attivo): per le azioni vale come se fosse mia. */
  managedByTeammate: boolean;
  /** Validità della riga (spec §11.1): valorizzata = ricorrente; il default del tipo arriva solo dal suggerimento. */
  validityMonths: number | null;
  /** Ancora del rinnovo: override della riga, altrimenti default del tipo. */
  recurrenceAnchor: RecurrenceAnchorValue | null;
  /** Il tipo ha solo un promemoria (durata regionale): la validità non è dedotta dal catalogo. */
  warningOnly: boolean;
  /** Spec §11.3: unica scaduta gestita da chi legge, da rinnovare o chiudere. */
  awaitingDecision: boolean;
  /** Valorizzato solo nella vista delle archiviate (`archived=true`); `null` sulle righe attive. */
  archivedAt?: string | null;
  /** Motivo di archiviazione: `NO_LONGER_NEEDED` (Chiusa), `MANUAL`, `SUPERSEDED`, `MEMBER_LEFT`. */
  archivedReason?: string | null;
  /** `true` solo se a archiviare la scadenza è stato il consulente che legge; falso per il cliente o il sistema. */
  archivedByMe?: boolean;
}

export interface ConsultantDeadlineListResponse {
  items: ConsultantDeadlineRow[];
  total: number;
  page: number;
  pageSize: number;
}

export type ConsultantDigestDeliveryStatus = 'QUEUED' | 'SENT' | 'FAILED';

export interface ConsultantDeadlineDigestStatusResponse {
  latest: {
    dayIso: string;
    status: ConsultantDigestDeliveryStatus;
    clientCount: number;
    createdAt: string;
  } | null;
  /** True quando l'ultimo digest è FAILED (rimbalzo, reclamo o invio fallito). */
  hasUndelivered: boolean;
}
